<template>
  <section class="hero-carousel" aria-label="Featured stories carousel">
    <article
      v-for="(slide, index) in slides"
      :key="slide.title"
      :class="['hero-card', slotClass(index)]"
    >
      <img :src="slide.src" :alt="slide.title" decoding="async" draggable="false" />
    </article>

    <div class="hero-dots" aria-label="Select featured story">
      <button
        v-for="(slide, index) in slides"
        :key="`${slide.title}-dot`"
        :class="['hero-dot', { 'is-active': index === activeIndex }]"
        type="button"
        :aria-label="`Show ${slide.title}`"
        :aria-current="index === activeIndex ? 'true' : undefined"
        @click="setSlide(index)"
      />
    </div>
  </section>
</template>

<script setup lang="ts">
import { onMounted, onUnmounted, ref } from 'vue'

import type { HeroSlide } from '../../data/home2026'

const props = defineProps<{
  slides: HeroSlide[]
}>()

const activeIndex = ref(0)
let heroTimer: number | undefined

const slotClass = (index: number) => {
  const total = props.slides.length
  const offset = (index - activeIndex.value + total) % total

  if (offset === 0) return 'is-center'
  if (offset === 1) return 'is-right'
  if (offset === total - 1) return 'is-left'
  return 'is-hidden'
}

const setSlide = (index: number) => {
  activeIndex.value = index
}

onMounted(() => {
  if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches && props.slides.length > 1) {
    heroTimer = window.setInterval(() => {
      activeIndex.value = (activeIndex.value + 1) % props.slides.length
    }, 5000)
  }
})

onUnmounted(() => {
  if (heroTimer !== undefined) {
    window.clearInterval(heroTimer)
  }
})
</script>

<style scoped>
.hero-carousel {
  position: absolute;
  z-index: 1;
  top: var(--hero-top);
  left: 50%;
  width: 100vw;
  height: var(--hero-height);
  overflow: hidden;
  background: #000;
  contain: layout paint style;
  perspective: var(--hero-perspective);
  perspective-origin: 50% 50%;
  transform: translateX(-50%);
}

.hero-card {
  position: absolute;
  top: var(--hero-card-y);
  overflow: hidden;
  border-radius: var(--radius-30);
  background: #080808;
  backface-visibility: hidden;
  transition:
    left 760ms cubic-bezier(0.19, 1, 0.22, 1),
    top 760ms cubic-bezier(0.19, 1, 0.22, 1),
    width 760ms cubic-bezier(0.19, 1, 0.22, 1),
    height 760ms cubic-bezier(0.19, 1, 0.22, 1),
    opacity 460ms ease,
    filter 460ms ease,
    transform 760ms cubic-bezier(0.19, 1, 0.22, 1);
  will-change: left, top, width, height, opacity, transform;
}

.hero-card::after {
  position: absolute;
  inset: 0;
  z-index: 2;
  border-radius: inherit;
  box-shadow: inset 0 3px 0 #000;
  content: '';
  pointer-events: none;
}

.hero-card img {
  position: absolute;
  inset: -1px;
  z-index: 1;
  display: block;
  width: calc(100% + 2px);
  max-width: none;
  height: calc(100% + 2px);
  object-fit: cover;
  user-select: none;
}

.hero-card.is-center {
  top: var(--hero-card-y);
  left: var(--hero-card-center-left);
  z-index: 3;
  width: var(--hero-card-center-width);
  height: var(--hero-card-height);
  opacity: 1;
  box-shadow: 0 var(--hero-shadow-y) var(--hero-shadow-blur) rgb(255 255 255 / 20%);
  transform: translate3d(0, 0, 0) scale(1);
}

.hero-card.is-left,
.hero-card.is-right {
  z-index: 2;
  width: var(--hero-card-side-width);
  height: var(--hero-card-height);
  opacity: 1;
  filter: none;
  box-shadow: none;
  transform: translate3d(0, 0, 0);
}

.hero-card.is-left {
  left: var(--hero-card-side-left);
  border-radius: 0 var(--radius-30) var(--radius-30) 0;
  transform-origin: left center;
  transform: translateX(calc(var(--hero-card-side-gap) * -1)) rotateY(30deg);
}

.hero-card.is-right {
  left: var(--hero-card-right-left);
  border-radius: var(--radius-30) 0 0 var(--radius-30);
  transform-origin: right center;
  transform: translateX(var(--hero-card-side-gap)) rotateY(-30deg);
}

.hero-card.is-left img {
  transform-origin: right center;
}

.hero-card.is-right img {
  transform-origin: left center;
}

.hero-card.is-left::after {
  background: linear-gradient(90deg, rgb(0 0 0 / 60%) 0%, rgb(0 0 0 / 20%) 100%);
}

.hero-card.is-right::after {
  background: linear-gradient(90deg, rgb(0 0 0 / 14%) 0%, rgb(0 0 0 / 4%) 100%);
}

.hero-card.is-hidden {
  left: var(--hero-card-hidden-left);
  z-index: 0;
  width: var(--hero-card-side-width);
  height: var(--hero-card-height);
  opacity: 0;
  transform: scale(0.92);
  pointer-events: none;
}

.hero-dots {
  position: absolute;
  z-index: 5;
  top: var(--hero-dot-y);
  left: 0;
  display: flex;
  width: 100%;
  justify-content: center;
  gap: var(--hero-dot-gap);
}

.hero-dot {
  width: var(--hero-dot-width);
  height: var(--hero-dot-height);
  padding: 0;
  border: 0;
  border-radius: 999px;
  background: rgb(119 119 119 / 58%);
  cursor: pointer;
  transition:
    width 220ms ease,
    background-color 220ms ease;
}

.hero-dot.is-active {
  width: var(--hero-dot-active-width);
  background: rgb(218 218 218 / 82%);
}

.hero-dot:focus-visible {
  outline: var(--focus-outline-width) solid rgb(138 188 249 / 90%);
  outline-offset: var(--focus-outline-offset);
}

@media (prefers-reduced-motion: reduce) {
  .hero-card,
  .hero-dot {
    transition: none;
  }
}
</style>
